import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import api from '@/app/api/axios.js';
import { nextTick, shallowRef } from 'vue';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';

const userIds = shallowRef([]);
const profilePictures = shallowRef({});
const avatarIds = shallowRef([]);
const avatarUrls = shallowRef({});

export const useSupportMessageReplies = () => {
    return {
        profilePictures,
        avatarUrls,
    };
};

export const clearSupportAvatarCache = () => {
    profilePictures.value = {};
    avatarUrls.value = {};
};

const fetchProfilePictures = async () => {
    const uncachedIds = [];
    const promiseList = [];

    for (let i = 0; i < userIds.value.length; i++) {
        if (profilePictures.value[userIds.value[i]]) {
            continue;
        }

        uncachedIds.push(userIds.value[i]);
        promiseList.push(
            api.get(`/general/users/${userIds.value[i]}/profile-picture`, {
                responseType: 'blob',
            })
        );
    }

    return Promise.allSettled(promiseList)
        .then((responses) => {
            for (let i = 0; i < responses.length; i++) {
                // skip rejected promises, use placeholder instead
                if (responses[i].status === 'rejected') {
                    continue;
                }

                // skip non-200 status codes (204 means no profile picture)
                if (responses[i].value.status !== 200) {
                    continue;
                }

                profilePictures.value[uncachedIds[i]] = responses[i].value.data;
            }
        })
        .catch((error) => {
            console.error(error);
        });
};

const fetchOperatorAvatars = () => {
    const uncachedIds = [];
    const promiseList = [];

    for (let i = 0; i < avatarIds.value.length; i++) {
        if (avatarUrls.value[avatarIds.value[i]]) {
            continue;
        }

        uncachedIds.push(avatarIds.value[i]);
        promiseList.push(
            api.get(avatarIds.value[i], {
                responseType: 'blob',
            })
        );
    }

    return Promise.allSettled(promiseList)
        .then((responses) => {
            for (let i = 0; i < responses.length; i++) {
                // skip rejected promises, use placeholder instead
                if (responses[i].status === 'rejected') {
                    continue;
                }

                // skip non-200 status codes (204 means no profile picture)
                if (responses[i].value.status !== 200) {
                    continue;
                }

                avatarUrls.value[uncachedIds[i]] = responses[i].value.data;
            }
        })
        .catch((error) => {
            console.error(error);
        });
};

const fetchSupportMessageReplies = async ({ pageParam = 1 }, supportMessageId) => {
    const { data } = await api.get(`/general/support-messages/${supportMessageId}/replies`, {
        params: {
            page: pageParam,
            per_page: 50,
        },
    });

    const currentPage = data.meta.current_page;
    const lastPage = data.meta.last_page;

    userIds.value = [];
    avatarIds.value = [];

    data.data.forEach((reply) => {
        // Add attachments to each reply
        reply.attachments = [];

        if (reply.relationships.attachments) {
            reply.relationships.attachments.data.forEach((attachment) => {
                const attachmentData = data.included.find(
                    (item) => item.id === attachment.id && item.type === 'attachment'
                );

                if (attachmentData) {
                    reply.attachments.push(attachmentData);
                }
            });
        }

        // Add profile picture if exists
        if (
            !reply.attributes.is_operator &&
            reply.relationships.user?.data?.id &&
            !userIds.value.includes(reply.relationships.user.data.id)
        ) {
            userIds.value.push(reply.relationships.user.data.id);
        }

        // Add operator avatar if exists
        if (
            reply.attributes.is_operator &&
            reply.attributes.operator_avatar &&
            !avatarIds.value.includes(reply.attributes.operator_avatar)
        ) {
            avatarIds.value.push(reply.attributes.operator_avatar);
        }
    });

    await Promise.all([fetchProfilePictures(), fetchOperatorAvatars()]);

    return {
        data: [...data.data].reverse(),
        cursor: currentPage < lastPage ? currentPage + 1 : undefined,
    };
};

export const useSupportMessageRepliesQuery = (supportMessageId) => {
    return useInfiniteQuery({
        queryKey: ['support-message-replies', supportMessageId],
        queryFn: (data) => fetchSupportMessageReplies(data, supportMessageId),
        select: (data) => ({
            pages: [...data.pages].reverse(),
            pageParams: [...data.pageParams].reverse(),
        }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.cursor,
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
        notifyOnChangeProps: ['data'],
    });
};

export const useCreateSupportMessageReplyMutation = (supportMessageId) => {
    const queryClient = useQueryClient();
    const authStore = useAuthStore();
    const pendingId = nanoid();

    return useMutation({
        mutationFn: async (newReply) => {
            try {
                const response = await api.post(
                    `/general/support-messages/${supportMessageId}/replies`,
                    newReply.reply,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                response.data.data.attachments = [];

                return response.data;
            } catch (e) {
                unexpectedError(e);

                throw e;
            }
        },
        onMutate: async (newReply) => {
            // Cancel any outgoing refetches
            // So they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ['support-message-replies', supportMessageId] });

            // Snapshot the previous value
            const previousReplies = queryClient.getQueryData(['support-message-replies', supportMessageId]);

            // Create a new reply object
            const temporaryReply = {
                id: pendingId,
                type: 'supportMessageReply',
                placeholder: true,
                attributes: {
                    message: newReply.reply.message.replace(/\n/g, '<br />'),
                    created_at: dayjs().toISOString(),
                    is_operator: false,
                    operator_avatar: null,
                    name: authStore.fullName,
                },
                attachments: newReply.reply.attachments.map(() => ({
                    id: nanoid(),
                    placeholder: true,
                })),
                relationships: {
                    user: {
                        data: {
                            id: authStore.user.id,
                            type: 'user',
                        },
                    },
                    supportMessage: {
                        data: {
                            id: supportMessageId,
                            type: 'supportMessage',
                        },
                    },
                    attachments: {
                        data: [],
                    },
                },
            };

            // Optimistically update to the new value
            queryClient.setQueryData(['support-message-replies', supportMessageId], (oldData) => {
                const firstPage = oldData.pages[0];

                if (!firstPage) {
                    return undefined;
                }

                return {
                    ...oldData,
                    pages: [
                        {
                            ...firstPage,
                            data: [...firstPage.data, temporaryReply],
                        },
                        ...oldData.pages.slice(1),
                    ],
                };
            });

            void nextTick(() => newReply.scrollBottom());

            return { previousReplies };
        },
        onError: (err, variables, context) => {
            // Rollback to the previous value
            queryClient.setQueryData(['support-message-replies', supportMessageId], context.previousReplies);
        },
        onSettled: () => {
            // Refetch the support message replies
            void queryClient.invalidateQueries({ queryKey: ['support-message-replies', supportMessageId] });
        },
    });
};
